import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isFuture, isValid } from 'date-fns';
import { SxProps } from '@mui/system';
import Typography from '@mui/material/Typography';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { Button } from 'common/components/Buttons/Button.tsx';
import { Dialog } from 'common/components/Dialog/Dialog.tsx';
import { styled } from '@mui/material/styles';
import { DEFAULT_BORDER_RADIUS } from 'theme/constants.ts';

const StyledDatePicker = styled(DatePicker)(({ theme: { palette } }) => ({
  '& fieldset': {
    borderRadius: DEFAULT_BORDER_RADIUS,
  },
  '& .Mui-error': {
    color: `${palette.error.contrastText} !important`,
    border: 'none',
    '& fieldset': {
      borderColor: `${palette.error.contrastText} !important`,
    },
  },
}));

const subtitle: SxProps = {
  whiteSpace: 'pre-line',
  marginBottom: 5,
};

const datePicker: SxProps = {
  width: '100%',
  marginBottom: 5,
  '& label': { background: 'white', paddingRight: 5 },
};

type SummaryReportDialogProps = {
  isOpen?: boolean;
  disabled?: boolean;
  onClose: () => void;
  onDownloadReport: (date: string) => void;
};

const today = new Date();
const ninetyDaysAgo = new Date(today);
ninetyDaysAgo.setDate(today.getDate() - 90);

const isValidDate = (date: Date | null) => {
  const chosenDateIsBeforeNinetyDays = date ? new Date(date) < ninetyDaysAgo : false;
  const isFutureDate = date ? isFuture(new Date(date)) : false;
  const isValidDate = isValid(date);

  return isValidDate && !chosenDateIsBeforeNinetyDays && !isFutureDate;
};

export const SummaryReportDialog = ({
  isOpen,
  disabled,
  onClose,
  onDownloadReport,
}: SummaryReportDialogProps) => {
  const { t } = useTranslation();
  const [date, setDate] = useState<Date | null>(new Date());

  const disableClose = disabled;
  const disableConfirm = disabled || !isValidDate(date);

  const handleConfirm = () => {
    if (!isValidDate(date)) {
      return;
    }
    onDownloadReport(date!.toISOString());
  };

  const handleClose = () => {
    if (disableClose) {
      return;
    }

    setDate(new Date());
    onClose();
  };

  return (
    <Dialog
      open={!!isOpen}
      title={t('bulkProvisioning.errorReportDialogTitle')}
      onClose={handleClose}
      size="small"
      actions={
        <>
          <Button variant="secondary" disabled={disableClose} onClick={handleClose}>
            Cancel
          </Button>
          <Button variant="primary" disabled={disableConfirm} onClick={handleConfirm}>
            Confirm
          </Button>
        </>
      }
    >
      <Typography sx={subtitle} data-testid="error-report-dialog-description">
        {t('bulkProvisioning.errorReportDialogSubtitle')}
      </Typography>
      <StyledDatePicker
        label={t('bulkProvisioning.errorReportDateInputLabel')}
        value={date}
        onChange={(newValue) => setDate(newValue)}
        sx={datePicker}
        disableFuture
        minDate={ninetyDaysAgo}
        slotProps={{
          textField: {
            error: disableConfirm,
            helperText: disableConfirm ? t('bulkProvisioning.errorReportDatePickerError') : '',
            FormHelperTextProps: {
              id: 'error-report-dialog-datepicker-helper-text',
            },
            InputLabelProps: {
              id: 'error-report-dialog-datepicker-label',
            },
            inputProps: {
              'data-testid': 'error-report-dialog-datepicker',
            },
          },
        }}
      />
    </Dialog>
  );
};
